<template>
  <section class="relative bg-white w-full overflow-hidden">
    <!-- Bouton Éditer (admin) -->
    <div class="absolute top-4 right-4 z-30" v-if="isEditMode">
      <button
        @click="editHero"
        class="flex font-alt items-center gap-2 px-4 py-2 text-sm font-semibold text-ink-800 bg-white/90 border border-ink-200 rounded-xl shadow-sm hover:bg-ink-50 hover:text-brand-600 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
      >
        <i class="fa-solid fa-pen-to-square text-brand-600"></i>
        <span>Éditer</span>
      </button>
    </div>

    <!-- ===== Desktop full-bleed image (caché sur mobile) ===== -->
    <div class="hidden lg:block absolute inset-y-0 right-0 w-1/2 z-0">
      <div class="h-full">
        <div v-if="loading" class="w-full h-full bg-ink-100 animate-pulse"></div>

        <img
          v-else-if="resolvedImageUrl"
          :src="resolvedImageUrl"
          alt=""
          decoding="async"
          loading="eager"
          @load="imageLoaded = true"
          :class="imageLoaded ? 'opacity-100' : 'opacity-0'"
          class="w-full h-full object-cover object-[62%_50%] transition-opacity duration-700"
        />

        <div v-else class="w-full h-full bg-gradient-to-br from-ink-50 to-white"></div>

        <div class="pointer-events-none absolute inset-0 bg-gradient-to-l from-transparent via-black/10 to-black/25"></div>
      </div>
    </div>
    <!-- ===== /Desktop full-bleed ===== -->

    <!-- Contenu + structure -->
    <div class="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
      <!-- === Image MOBILE d'abord === -->
      <div class="block lg:hidden w-full mt-2 mb-8 sm:mb-10">
        <div
          class="relative w-full h-72 rounded-2xl overflow-hidden ring-1 ring-ink-200 shadow-[0_16px_40px_-24px_rgba(0,0,0,.35)]"
        >
          <div v-if="loading" class="absolute inset-0 bg-ink-100 animate-pulse"></div>

          <!-- ta version: background cover -->
          <div
            class="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
            :style="bgStyle"
            :class="bgClass"
          ></div>

          <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
        </div>
      </div>
      <!-- === /Image MOBILE === -->

      <div
        class="grid grid-cols-1 lg:grid-cols-[minmax(0,640px)_1fr]
               items-center gap-y-10
               lg:min-h-[clamp(600px,72vh,840px)] py-8 sm:py-10 lg:py-20"
      >
        <!-- Colonne Texte -->
        <div class="w-full">
          <div class="flex flex-col justify-center text-center lg:text-left lg:pr-24 xl:pr-32">
            <!-- Squelette -->
            <div v-if="loading" class="animate-pulse">
              <div class="h-10 sm:h-12 w-4/5 mx-auto lg:mx-0 bg-ink-100 rounded-lg"></div>
              <div class="mt-4 h-5 w-5/6 mx-auto lg:mx-0 bg-ink-100 rounded"></div>
              <div class="mt-2 h-5 w-2/3 mx-auto lg:mx-0 bg-ink-100 rounded"></div>
              <div class="mt-8 h-10 w-56 mx-auto lg:mx-0 bg-ink-100 rounded-full"></div>
            </div>

            <template v-else>
              <h1
                class="font-extrabold text-ink-900 leading-tight tracking-tight"
                :style="{ fontSize: 'clamp(2.25rem,4.2vw,3.5rem)' }"
              >
                {{ titreAffiche }}
              </h1>

              <p class="mt-6 text-[1.05rem] sm:text-lg text-ink-700 leading-relaxed max-w-[56ch] mx-auto lg:mx-0">
                {{ descriptionAffiche }}
              </p>

              <!-- Boutons -->
              <div class="mt-10 flex flex-col sm:flex-row justify-center lg:justify-start gap-3 sm:gap-4">
                <template v-for="(bouton, index) in heroButtons" :key="bouton.id || index">
                  <!-- Lien externe -->
                  <a
                    v-if="isExternal(bouton.action)"
                    :href="bouton.action || '#'"
                    target="_blank"
                    rel="noopener"
                    class="relative group inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-full overflow-hidden transition-all duration-300 shadow-[0_2px_10px_rgba(0,0,0,.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                    :class="buttonClass(bouton, index)"
                  >
                    <span class="relative z-10">{{ bouton.label || (getLangue === 'en' ? 'Discover' : 'Découvrir') }}</span>
                    <i class="fas fa-arrow-up-right-from-square text-xs relative z-10"></i>
                    <span class="absolute inset-0 rounded-full bg-black/10 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] pointer-events-none"></span>
                  </a>

                  <!-- Lien interne -->
                  <router-link
                    v-else
                    :to="localized(bouton.action || '#')"
                    class="relative group inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-full overflow-hidden transition-all duration-300 shadow-[0_2px_10px_rgba(0,0,0,.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                    :class="buttonClass(bouton, index)"
                  >
                    <span class="relative z-10">{{ bouton.label || (getLangue === 'en' ? 'Discover' : 'Découvrir') }}</span>
                    <i class="fas fa-arrow-right text-xs relative z-10 group-hover:translate-x-1 transition-transform"></i>
                    <span class="absolute inset-0 rounded-full bg-black/10 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] pointer-events-none"></span>
                  </router-link>
                </template>
              </div>
            </template>
          </div>
        </div>

        <!-- Spacer desktop (occupe la 2e colonne pendant que l'image est en full-bleed absolue) -->
        <div class="hidden lg:block"></div>
      </div>
    </div>
  </section>
</template>


<script>
import { mapGetters } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

// Base d’URL issue de l’instance axios centralisée
const API_BASE = String(process.env.VUE_APP_API_BASE || "").replace(/\/+$/, "");
const UPLOADS_ORIGIN = String(process.env.VUE_APP_UPLOADS_ORIGIN || API_BASE).replace(/\/+$/, "");


export default {
  name: "HeroComponent",
  props: {
    isEditMode: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    pageCode: { type: String, default: "home" },
  },
  data() {
    return {
      heroContent: null,
      heroButtons: [],
      heroImage: null, // { image_url? | url? | path? }
      loading: true,
      imageLoaded: false,
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),

    titreAffiche() {
      return this.heroContent?.titre || (this.getLangue === "en" ? "Welcome" : "Bienvenue");
    },
    descriptionAffiche() {
      return (
        this.heroContent?.description ||
        (this.getLangue === "en"
          ? "Your ideal partner to make your projects succeed."
          : "Votre partenaire idéal pour réussir vos projets.")
      );
    },

    // URL finale résolue pour l’image
    resolvedImageUrl() {
      const raw =
        this.heroImage?.image_url ||
        this.heroImage?.url ||
        this.heroImage?.path ||
        "";

      if (!raw) return "";

      // Si c’est un /uploads/..., préfixe avec UPLOADS_ORIGIN
      if (/^\/uploads\//i.test(raw)) {
        const base = String(UPLOADS_ORIGIN || "").replace(/\/+$/, "");
        return base ? `${base}${raw}` : raw;
      }

      // Si c’est déjà absolu (http/https), renvoie tel quel
      if (/^https?:\/\//i.test(raw)) return raw;

      // Sinon: URL relative → préfixe API_BASE
      const base = String(API_BASE || "").replace(/\/+$/, "");
      return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
    },

    hasImage() {
      return !!this.resolvedImageUrl;
    },

    // Style de fond: image si dispo, sinon dégradé
    bgStyle() {
      if (this.hasImage) {
        return { backgroundImage: `url(${this.resolvedImageUrl})` };
      }
      return { backgroundImage: "linear-gradient(135deg, #eaf1ff, #f5f7ff)" };
    },

    // Gestion de l’opacité / fade-in uniquement si image
    bgClass() {
      if (!this.hasImage) return "opacity-100";
      return this.imageLoaded ? "opacity-100" : "opacity-0";
    },
  },
  watch: {
    getLangue(newLang) {
      this.loadData(newLang);
    },
    resolvedImageUrl(newUrl, oldUrl) {
      if (newUrl !== oldUrl) {
        // (re)précharge la nouvelle image et ne montre qu'une fois prête
        this.preloadImage(newUrl);
      }
    },
  },
  methods: {
    isExternal(href) {
      return /^https?:\/\//i.test(String(href || ""));
    },
    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    localized(path) {
      return this.ensureLocalePrefix(path, this.getLangue);
    },

    preloadImage(src) {
      if (!src) {
        this.imageLoaded = true; // pas d'image -> gradient visible
        return;
      }
      this.imageLoaded = false; // on repart en fade-out

      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.onload = () => {
        // une frame plus tard pour garantir la transition
        requestAnimationFrame(() => { this.imageLoaded = true; });
      };
      img.onerror = () => { this.imageLoaded = true; }; // fallback: montre le gradient
      img.src = src;
    },

    async resolvePageId(lang) {
      const direct = Number(this.pageId);
      if (Number.isFinite(direct) && direct > 0) return direct;
      const resolved = await fetchPageIdByCode(this.pageCode || "home", lang || "fr");
      const n = Number(resolved?.page_id ?? resolved?.id ?? resolved);
      return Number.isFinite(n) && n > 0 ? n : null;
    },

    // async loadData(lang) {
    //   this.loading = true;
    //   try {
    //     const pageId = await this.resolvePageId(lang);
    //     if (!pageId) {
    //       this.heroContent = null;
    //       this.heroButtons = [];
    //       this.heroImage = null;
    //       // pas d'image -> on montre direct le gradient
    //       this.imageLoaded = true;
    //       this.loading = false;
    //       return;
    //     }

    //     // Utilise le client HTTP centralisé (baseURL = /api ou VUE_APP_API_BASE)
    //     const { data } = await http.get("hero", { params: { page_id: pageId } });

    //     const title = data?.title || null;
    //     const buttons = Array.isArray(data?.buttons) ? data.buttons : [];
    //     const images = Array.isArray(data?.images) ? data.images : [];

    //     this.heroContent = title
    //       ? { id: title.id, titre: title.titre || "", description: title.description || "" }
    //       : null;

    //     this.heroButtons = buttons.map((b, idx) => ({
    //       id: b.id,
    //       contenu_id: b.contenu_id,
    //       label: b.label || (this.getLangue === "en" ? "Discover" : "Découvrir"),
    //       action: b.action || "#",
    //       style: b.style || (idx === 0 ? "primary" : "secondary"),
    //     }));

    //     this.heroImage = images.length ? images[0] : null;

    //     // ne touche pas imageLoaded ici : le watcher resolvedImageUrl lancera preloadImage()
    //     if (!this.hasImage) this.imageLoaded = true; // gradient visible si pas d'image
    //   } catch (error) {
    //     console.error("[HeroComponent] loadData error:", error);
    //     this.heroContent = null;
    //     this.heroButtons = [];
    //     this.heroImage = null;
    //     this.imageLoaded = true; // évite rester en opacity-0
    //   } finally {
    //     this.loading = false;
    //   }
    // },
async loadData(lang) {
      // 🛡️ SÉCURITÉ : On nettoie et on vérifie la langue
      const l = String(lang || this.getLangue || this.$route.params.langue || "").trim();

      // Si pas de langue, on arrête tout de suite. 
      // Le "watch: { getLangue }" relancera cette fonction dès que la langue sera disponible.
      if (!l || l === "undefined" || l === "null") {
        return;
      }

      this.loading = true;
      try {
        // On utilise la variable 'l' validée
        const pageId = await this.resolvePageId(l);
        
        if (!pageId) {
          this.heroContent = null;
          this.heroButtons = [];
          this.heroImage = null;
          this.imageLoaded = true;
          this.loading = false;
          return;
        }

        const { data } = await http.get("hero", { params: { page_id: pageId } });

        const title = data?.title || null;
        const buttons = Array.isArray(data?.buttons) ? data.buttons : [];
        const images = Array.isArray(data?.images) ? data.images : [];

        this.heroContent = title
          ? { id: title.id, titre: title.titre || "", description: title.description || "" }
          : null;

        this.heroButtons = buttons.map((b, idx) => ({
          id: b.id,
          contenu_id: b.contenu_id,
          label: b.label || (l === "en" ? "Discover" : "Découvrir"), // Utilise 'l' ici aussi
          action: b.action || "#",
          style: b.style || (idx === 0 ? "primary" : "secondary"),
        }));

        this.heroImage = images.length ? images[0] : null;

        if (!this.hasImage) this.imageLoaded = true;
      } catch (error) {
        // Optionnel : ignorer les erreurs 400 silencieusement
        if (error.response && error.response.status !== 400) {
           console.error("[HeroComponent] loadData error:", error);
        }
        this.heroContent = null;
        this.heroButtons = [];
        this.heroImage = null;
        this.imageLoaded = true;
      } finally {
        this.loading = false;
      }
    },
    editHero() {
      this.$emit("edit", {
        heroTitle: this.heroContent,
        heroButtons: this.heroButtons,
        heroImage: this.heroImage,
      });
    },

    buttonClass(bouton, index) {
      const isPrimary = (bouton.style || (index === 0 ? "primary" : "secondary")) === "primary";
      return isPrimary
        ? "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-300"
        : "bg-white text-ink-800 border border-ink-200 hover:bg-ink-50 focus:ring-ink-300";
    },
  },
  mounted() {
    this.loadData(this.getLangue);
  },
};
</script>


<style scoped>
@media (prefers-reduced-motion: reduce) {

  .transition,
  .transition-all,
  .transition-opacity,
  .transition-transform {
    transition: none !important;
  }
}
</style>